import { ReactElement, useState } from 'react';
import { Svg, TextField, Tooltip } from '@hopskipdrive/react-components';
import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
  Box,
  MenuItem,
} from '@mui/material';

import { CopyToClipboard } from '../../../shared/components/CopyToClipboard';
import { IncidentIndividual } from '../../types/incidents';

import {
  RiderDriverChip,
  RiderDriverGroup,
  RiderDriverGroupHeader,
  RiderDriverInputContainer,
  RiderDriverListItem,
} from './RiderDriverSearch.styles';

export interface RiderDriverSearchProps {
  options: IncidentIndividual[] | null;
  value: IncidentIndividual | IncidentIndividual[] | null;
  onChange: (value: IncidentIndividual | IncidentIndividual[] | null) => void;

  multiple?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
}

const RiderDriverSearch = ({
  options,
  value,
  onChange,
  multiple,
  placeholder,
  error,
  helperText,
  ...rest
}: RiderDriverSearchProps): ReactElement => {
  const filterOptionsOnSearch = (options: IncidentIndividual[]) => {
    if (inputValue) {
      const lowercasedSearchString = inputValue.toLowerCase();

      return options.filter((option) => {
        return (
          option.firstName.toLowerCase().includes(lowercasedSearchString) ||
          option.lastName.toLowerCase().includes(lowercasedSearchString) ||
          combineFirstAndLastName(option).toLowerCase().includes(lowercasedSearchString) ||
          option.slug?.toLowerCase().includes(lowercasedSearchString)
        );
      });
    }

    return options;
  };

  const combineFirstAndLastName = (assignee: IncidentIndividual | null) => {
    return assignee ? assignee.firstName + ' ' + assignee.lastName : '';
  };
  const combineFirstAndLastNameSlug = (assignee: IncidentIndividual | null) => {
    return assignee && assignee.slug
      ? assignee.firstName + ' ' + assignee.lastName + ' ' + '(...' + assignee.slug.slice(-5) + ')'
      : '';
  };

  const [inputValue, setInputValue] = useState<string>();

  return (
    <Autocomplete
      fullWidth
      multiple={multiple}
      options={options || []}
      filterOptions={filterOptionsOnSearch}
      groupBy={(option: IncidentIndividual) => (option.removed ? 'Removed' : 'Active')}
      value={value || null}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      // handleSelected={onChange}
      getOptionLabel={(option) => (typeof option === 'string' ? option : combineFirstAndLastName(option) || '')}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <RiderDriverInputContainer className={(value ? 'hasValue ' : 'noValue ') + (multiple ? 'multiple' : 'single')}>
          <Svg className="search" icon="search" />
          {!multiple && value && (
            <Tooltip
              title={
                <CopyToClipboard
                  toastMessage="Slug Copied"
                  text={(value as IncidentIndividual).slug || ''}
                  variant="subtitle1"
                />
              }
              leaveDelay={400}
              placement="top"
            >
              <span className="single-container">
                <RiderDriverChip
                  label={combineFirstAndLastName(value as IncidentIndividual)}
                  key={(value as IncidentIndividual)?.slug}
                  onDelete={() => {
                    setInputValue('');
                    onChange(null);
                  }}
                />
              </span>
            </Tooltip>
          )}
          <TextField
            {...params}
            fullWidth
            style={{ maxHeight: 'unset' }}
            variant="outlined"
            placeholder={placeholder}
            value={inputValue}
            error={error}
            helperText={helperText}
            onChange={(e) => {
              setInputValue(e?.target?.value || '');
            }}
          />
        </RiderDriverInputContainer>
      )}
      renderOption={(props, option) => {
        return (
          <MenuItem {...props} key={option.id}>
            <RiderDriverListItem variant="body2">{combineFirstAndLastNameSlug(option)}</RiderDriverListItem>
          </MenuItem>
        );
      }}
      renderGroup={(params: AutocompleteRenderGroupParams) => {
        return (
          <RiderDriverGroup key={params.key}>
            {params.group && <RiderDriverGroupHeader variant="subtitle2">{params.group}</RiderDriverGroupHeader>}
            {params.children}
          </RiderDriverGroup>
        );
      }}
      renderTags={(tag, getTagProps) => {
        return tag.map((option, index) => (
          <Tooltip
            key={option.slug}
            title={<CopyToClipboard toastMessage="Slug Copied" text={option.slug || ''} variant="subtitle1" />}
            leaveDelay={400}
            placement="top"
          >
            <Box key={option.slug}>
              <RiderDriverChip {...getTagProps({ index })} label={combineFirstAndLastName(option)} key={option.slug} />
            </Box>
          </Tooltip>
        ));
      }}
      {...rest}
    />
  );
};

export default RiderDriverSearch;
